<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Upload Locations Dataset</h2>
        <button @click="$emit('close')" class="btn-close">×</button>
      </div>
      
      <div class="modal-body">
        <!-- Step 1: File Selection -->
        <div v-if="step === 1" class="upload-step">
          <h3>Select CSV File</h3>
          <div 
            class="file-drop-zone"
            :class="{ 'drag-over': isDragOver }"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="handleFileDrop"
            @click="triggerFileInput"
          >
            <input 
              ref="fileInput" 
              type="file" 
              accept=".csv"
              @change="handleFileSelect"
              style="display: none"
            />
            
            <div v-if="!selectedFile" class="drop-zone-content">
              <div class="upload-icon">📁</div>
              <p>Drag and drop a CSV file here or click to select</p>
              <p class="file-types">Supported: CSV files only</p>
            </div>
            
            <div v-else class="file-selected">
              <div class="file-icon">📄</div>
              <div class="file-info">
                <div class="file-name">{{ selectedFile.name }}</div>
                <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
              </div>
              <button @click.stop="removeFile" class="btn-remove">×</button>
            </div>
          </div>
          
          <div class="step-actions">
            <button @click="$emit('close')" class="btn btn-secondary">Cancel</button>
            <button @click="processFile" :disabled="!selectedFile" class="btn btn-primary">
              Next
            </button>
          </div>
        </div>

        <!-- Step 2: Column Mapping -->
        <div v-if="step === 2" class="mapping-step">
          <h3>Map CSV Columns to Location Fields</h3>
          
          <div class="mapping-form">
            <div class="mapping-instructions">
              <p>Map your CSV columns to the required location fields:</p>
            </div>
            
            <div class="field-mappings">
              <div class="mapping-group required">
                <label>Name (Required)</label>
                <select v-model="fieldMapping.name" class="form-select">
                  <option value="">Select column for location name</option>
                  <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                </select>
              </div>
              
              <div class="mapping-group required">
                <label>Coordinates (Required)</label>
                <div class="coordinate-options">
                  <div class="option-group">
                    <label class="radio-label">
                      <input type="radio" v-model="coordinateMode" value="combined" />
                      Single coordinates column
                    </label>
                    <select 
                      v-if="coordinateMode === 'combined'" 
                      v-model="fieldMapping.geometry" 
                      class="form-select"
                    >
                      <option value="">Select coordinates column</option>
                      <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </div>
                  
                  <div class="option-group">
                    <label class="radio-label">
                      <input type="radio" v-model="coordinateMode" value="separate" />
                      Separate latitude/longitude columns
                    </label>
                    <div v-if="coordinateMode === 'separate'" class="lat-lng-selects">
                      <div class="lat-lng-field">
                        <label>Latitude:</label>
                        <select v-model="fieldMapping.latitude" class="form-select">
                          <option value="">Select latitude column</option>
                          <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                        </select>
                      </div>
                      <div class="lat-lng-field">
                        <label>Longitude:</label>
                        <select v-model="fieldMapping.longitude" class="form-select">
                          <option value="">Select longitude column</option>
                          <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div class="option-group">
                    <label class="radio-label">
                      <input type="radio" v-model="coordinateMode" value="geocoding" />
                      Geocode from address data
                    </label>
                    <div v-if="coordinateMode === 'geocoding'" class="geocoding-options">
                      <div class="address-mode-selection">
                        <label class="radio-label">
                          <input type="radio" v-model="addressMode" value="single" />
                          Single address column
                        </label>
                        <div v-if="addressMode === 'single'" class="address-field-select">
                          <label>Address Column:</label>
                          <select v-model="fieldMapping.fullAddress" class="form-select">
                            <option value="">Select address column</option>
                            <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                          </select>
                        </div>
                        
                        <label class="radio-label">
                          <input type="radio" v-model="addressMode" value="components" />
                          Multiple address components
                        </label>
                        <div v-if="addressMode === 'components'" class="address-components">
                          <div class="component-field">
                            <label>Street:</label>
                            <select v-model="fieldMapping.street" class="form-select">
                              <option value="">Select street column</option>
                              <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                            </select>
                          </div>
                          <div class="component-field">
                            <label>City:</label>
                            <select v-model="fieldMapping.city" class="form-select">
                              <option value="">Select city column</option>
                              <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                            </select>
                          </div>
                          <div class="component-field">
                            <label>State/Province:</label>
                            <select v-model="fieldMapping.state" class="form-select">
                              <option value="">Select state column</option>
                              <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                            </select>
                          </div>
                          <div class="component-field">
                            <label>Country:</label>
                            <select v-model="fieldMapping.country" class="form-select">
                              <option value="">Select country column</option>
                              <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                            </select>
                          </div>
                          <div class="component-field">
                            <label>Postal Code:</label>
                            <select v-model="fieldMapping.postalCode" class="form-select">
                              <option value="">Select postal code column</option>
                              <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      <div class="geocoding-settings">
                        <div class="form-group">
                          <label>Country Code (Optional):</label>
                          <input 
                            v-model="geocodingConfig.countryCode" 
                            type="text" 
                            placeholder="e.g., US, TR, DE"
                            maxlength="2"
                            class="form-input small"
                          />
                          <small>2-letter country code to improve geocoding accuracy</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="mapping-group optional">
                <label>Type (Optional)</label>
                <select v-model="fieldMapping.type" class="form-select">
                  <option value="">Select column for location type (defaults to 'demand')</option>
                  <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                </select>
              </div>
              
              <div class="mapping-group optional">
                <label>Demand (Optional)</label>
                <select v-model="fieldMapping.demand" class="form-select">
                  <option value="">Select column for demand quantity</option>
                  <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                </select>
              </div>
              
              <div class="mapping-group optional">
                <label>Time Window Start (Optional)</label>
                <select v-model="fieldMapping.timeWindowStart" class="form-select">
                  <option value="">Select column for time window start</option>
                  <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                </select>
              </div>
              
              <div class="mapping-group optional">
                <label>Time Window End (Optional)</label>
                <select v-model="fieldMapping.timeWindowEnd" class="form-select">
                  <option value="">Select column for time window end</option>
                  <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                </select>
              </div>
              
              <div class="mapping-group optional">
                <label>Vehicle Capability (Optional)</label>
                <select v-model="fieldMapping.vehicleCapability" class="form-select">
                  <option value="">Select column for vehicle capability</option>
                  <option v-for="col in csvColumns" :key="col" :value="col">{{ col }}</option>
                </select>
              </div>
            </div>
            
            <div class="preview-section">
              <h4>Data Preview</h4>
              <div class="preview-info">
                <span>Records: {{ csvData.length }}</span>
                <span>Columns: {{ csvColumns.length }}</span>
              </div>
              
              <div class="preview-table" v-if="csvData.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th v-for="col in csvColumns.slice(0, 6)" :key="col">
                        {{ col }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in csvData.slice(0, 3)" :key="index">
                      <td v-for="col in csvColumns.slice(0, 6)" :key="col">
                        {{ formatPreviewValue(row[col]) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p v-if="csvColumns.length > 6" class="preview-note">
                  Showing first 6 columns...
                </p>
              </div>
            </div>
          </div>
          
          <div class="step-actions">
            <button @click="step = 1" class="btn btn-secondary">Back</button>
            <button @click="validateMapping" :disabled="!isMappingValid" class="btn btn-primary">
              Next
            </button>
          </div>
        </div>

        <!-- Step 3: Collection Configuration -->
        <div v-if="step === 3" class="configuration-step">
          <h3>Configure Collection</h3>
          
          <div class="config-form">
            <div class="form-group">
              <label>Collection Name *</label>
              <input 
                v-model="collectionConfig.name" 
                type="text" 
                placeholder="Enter collection name"
                class="form-input"
                required
              />
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea 
                v-model="collectionConfig.description" 
                placeholder="Enter description (optional)"
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
            
            <div class="validation-summary">
              <h4>Upload Summary</h4>
              <div class="summary-stats">
                <div class="stat-item">
                  <span class="stat-label">Total Records:</span>
                  <span class="stat-value">{{ csvData.length }}</span>
                </div>
                <div class="stat-item" v-if="validationResults.valid > 0">
                  <span class="stat-label">Valid Locations:</span>
                  <span class="stat-value success">{{ validationResults.valid }}</span>
                </div>
                <div class="stat-item" v-if="validationResults.errors.length > 0">
                  <span class="stat-label">Errors:</span>
                  <span class="stat-value error">{{ validationResults.errors.length }}</span>
                </div>
              </div>
              
              <div v-if="validationResults.errors.length > 0" class="validation-errors">
                <h5>Validation Errors</h5>
                <ul class="error-list">
                  <li v-for="(error, index) in validationResults.errors.slice(0, 5)" :key="index">
                    {{ error }}
                  </li>
                  <li v-if="validationResults.errors.length > 5">
                    ... and {{ validationResults.errors.length - 5 }} more errors
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="step-actions">
            <button @click="step = 2" class="btn btn-secondary">Back</button>
            <button 
              @click="uploadCollection" 
              :disabled="!collectionConfig.name || validationResults.valid === 0" 
              class="btn btn-primary"
            >
              Upload Collection
            </button>
          </div>
        </div>

        <!-- Step 4: Upload Progress -->
        <div v-if="step === 4" class="loading-step">
          <div class="loading-content">
            <div class="spinner" v-if="!uploadProgress?.current"></div>
            <h3>{{ uploadProgress?.message || 'Uploading Collection...' }}</h3>
            
            <!-- Progress Bar -->
            <div v-if="uploadProgress?.total" class="progress-section">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: `${((uploadProgress?.current || 0) / (uploadProgress?.total || 1)) * 100}%` }"
                ></div>
              </div>
              <div class="progress-text">
                {{ uploadProgress?.current || 0 }} / {{ uploadProgress?.total || 0 }} locations processed
                <span v-if="(uploadProgress?.total || 0) > 0">
                  ({{ Math.round(((uploadProgress?.current || 0) / (uploadProgress?.total || 1)) * 100) }}%)
                </span>
              </div>
            </div>
            
            <p v-if="!uploadProgress?.current">Please wait while we process your location data.</p>
            
            <!-- Error section -->
            <div v-if="uploadProgress?.errors && uploadProgress?.errors?.length > 0" class="progress-errors">
              <details>
                <summary>{{ uploadProgress?.errors?.length || 0 }} processing errors (click to expand)</summary>
                <ul>
                  <li v-for="(error, index) in (uploadProgress?.errors || []).slice(0, 10)" :key="index">
                    {{ error }}
                  </li>
                  <li v-if="(uploadProgress?.errors?.length || 0) > 10">
                    ... and {{ (uploadProgress?.errors?.length || 0) - 10 }} more
                  </li>
                </ul>
              </details>
            </div>
          </div>
        </div>

        <!-- Step 5: Success -->
        <div v-if="step === 5" class="success-step">
          <div class="success-content">
            <div class="success-icon">✅</div>
            <h3>Upload Successful!</h3>
            <p>Your location collection has been created and is now available.</p>
            
            <div class="upload-summary">
              <div class="summary-item">
                <label>Collection Name:</label>
                <span>{{ collectionConfig.name }}</span>
              </div>
              <div class="summary-item">
                <label>Locations Created:</label>
                <span>{{ uploadResult.locations_created || 0 }}</span>
              </div>
              <div class="summary-item" v-if="uploadResult.errors && uploadResult.errors.length > 0">
                <label>Errors:</label>
                <span>{{ uploadResult.errors.length }} records had issues</span>
              </div>
            </div>
          </div>
          
          <div class="step-actions">
            <button @click="finishUpload" class="btn btn-primary">Done</button>
          </div>
        </div>

        <!-- Error Display -->
        <div v-if="error" class="error-message">
          <div class="error-icon">⚠️</div>
          <div class="error-text">
            <strong>Error:</strong> {{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import LocationsService from '../services/LocationsService'
import GeocodingService from '../services/GeocodingService'

export default {
  name: 'LocationsUploadModal',
  emits: ['close', 'collection-uploaded'],
  setup(props, { emit }) {
    const step = ref(1)
    const selectedFile = ref(null)
    const isDragOver = ref(false)
    const error = ref('')
    const fileInput = ref(null)
    
    const csvData = ref([])
    const csvColumns = ref([])
    const coordinateMode = ref('combined')
    const addressMode = ref('single')
    const uploadResult = ref({})
    const geocodingResults = ref([])
    const showGeocodingPreview = ref(false)
    const uploadProgress = reactive({
      current: 0,
      total: 0,
      message: '',
      status: '',
      errors: []
    })
    
    const fieldMapping = reactive({
      name: '',
      geometry: '',
      latitude: '',
      longitude: '',
      fullAddress: '',
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      type: '',
      demand: '',
      timeWindowStart: '',
      timeWindowEnd: '',
      vehicleCapability: ''
    })
    
    const geocodingConfig = reactive({
      countryCode: ''
    })
    
    const collectionConfig = reactive({
      name: '',
      description: ''
    })
    
    const validationResults = reactive({
      valid: 0,
      errors: []
    })

    const isMappingValid = computed(() => {
      if (!fieldMapping.name) return false
      
      if (coordinateMode.value === 'combined') {
        return Boolean(fieldMapping.geometry)
      } else if (coordinateMode.value === 'separate') {
        return Boolean(fieldMapping.latitude && fieldMapping.longitude)
      } else if (coordinateMode.value === 'geocoding') {
        if (addressMode.value === 'single') {
          return Boolean(fieldMapping.fullAddress)
        } else {
          // Components mode - at least city or street should be provided
          return Boolean(fieldMapping.street || fieldMapping.city)
        }
      }
      
      return false
    })

    const handleFileDrop = (event) => {
      isDragOver.value = false
      const files = event.dataTransfer.files
      if (files.length > 0) {
        selectFile(files[0])
      }
    }

    const handleFileSelect = (event) => {
      const files = event.target.files
      if (files.length > 0) {
        selectFile(files[0])
      }
    }

    const triggerFileInput = () => {
      fileInput.value.click()
    }

    const selectFile = (file) => {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        error.value = 'Invalid file type. Please select a CSV file.'
        return
      }

      selectedFile.value = file
      collectionConfig.name = file.name.replace(/\.[^/.]+$/, '')
      error.value = ''
    }

    const removeFile = () => {
      selectedFile.value = null
      fileInput.value.value = ''
    }

    const processFile = async () => {
      if (!selectedFile.value) return

      try {
        const text = await readFileAsText(selectedFile.value)
        const result = parseCSV(text)
        
        csvData.value = result.data
        csvColumns.value = result.columns
        
        // Auto-detect coordinate columns
        autoDetectColumns()
        
        step.value = 2
      } catch (err) {
        error.value = err.message
      }
    }

    const readFileAsText = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsText(file)
      })
    }

    const parseCSV = (text) => {
      const lines = text.split('\n').filter(line => line.trim())
      if (lines.length < 2) {
        throw new Error('CSV file must have at least a header row and one data row')
      }
      
      const headers = lines[0].split(',').map(h => h.trim().replace(/['"]/g, ''))
      const data = []
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''))
        const row = {}
        headers.forEach((header, index) => {
          row[header] = values[index] || ''
        })
        data.push(row)
      }
      
      return { columns: headers, data }
    }

    const autoDetectColumns = () => {
      const nameColumns = ['name', 'title', 'location', 'location_name']
      const coordColumns = ['coordinates', 'coords', 'geometry', 'latlng', 'lat_lng']
      const latColumns = ['lat', 'latitude', 'y']
      const lngColumns = ['lng', 'longitude', 'lon', 'x']
      const typeColumns = ['type', 'location_type', 'category']
      const demandColumns = ['demand', 'quantity', 'amount', 'volume']
      
      const lowerColumns = csvColumns.value.map(col => col.toLowerCase())
      
      // Auto-detect name column
      for (const nameCol of nameColumns) {
        const index = lowerColumns.indexOf(nameCol)
        if (index !== -1) {
          fieldMapping.name = csvColumns.value[index]
          break
        }
      }
      
      // Auto-detect coordinate columns
      for (const coordCol of coordColumns) {
        const index = lowerColumns.indexOf(coordCol)
        if (index !== -1) {
          fieldMapping.geometry = csvColumns.value[index]
          coordinateMode.value = 'combined'
          break
        }
      }
      
      // If no combined coordinate column, look for separate lat/lng
      if (!fieldMapping.geometry) {
        let hasLatLng = false
        for (const latCol of latColumns) {
          const index = lowerColumns.indexOf(latCol)
          if (index !== -1) {
            fieldMapping.latitude = csvColumns.value[index]
            coordinateMode.value = 'separate'
            hasLatLng = true
            break
          }
        }
        
        for (const lngCol of lngColumns) {
          const index = lowerColumns.indexOf(lngCol)
          if (index !== -1) {
            fieldMapping.longitude = csvColumns.value[index]
            coordinateMode.value = 'separate'
            hasLatLng = true
            break
          }
        }
        
        // If no coordinates found, try to detect address columns
        if (!hasLatLng) {
          const addressSuggestions = GeocodingService.autoDetectAddressColumns(csvColumns.value)
          
          if (addressSuggestions.fullAddress) {
            fieldMapping.fullAddress = addressSuggestions.fullAddress
            coordinateMode.value = 'geocoding'
            addressMode.value = 'single'
          } else if (addressSuggestions.street || addressSuggestions.city) {
            // Use components mode
            fieldMapping.street = addressSuggestions.street || ''
            fieldMapping.city = addressSuggestions.city || ''
            fieldMapping.state = addressSuggestions.state || ''
            fieldMapping.country = addressSuggestions.country || ''
            fieldMapping.postalCode = addressSuggestions.postal_code || ''
            coordinateMode.value = 'geocoding'
            addressMode.value = 'components'
          }
        }
      }
      
      // Auto-detect other columns
      for (const typeCol of typeColumns) {
        const index = lowerColumns.indexOf(typeCol)
        if (index !== -1) {
          fieldMapping.type = csvColumns.value[index]
          break
        }
      }
      
      for (const demandCol of demandColumns) {
        const index = lowerColumns.indexOf(demandCol)
        if (index !== -1) {
          fieldMapping.demand = csvColumns.value[index]
          break
        }
      }
    }

    const validateMapping = async () => {
      if (!isMappingValid.value) {
        error.value = 'Please map required fields (Name and Coordinates/Address)'
        return
      }
      
      if (coordinateMode.value === 'geocoding') {
        // Handle geocoding validation
        await validateWithGeocoding()
      } else {
        // Handle coordinate-based validation
        validateCoordinates()
      }
    }
    
    const validateCoordinates = () => {
      const errors = []
      let validCount = 0
      
      csvData.value.forEach((row, index) => {
        const rowNum = index + 1
        
        // Check name
        if (!row[fieldMapping.name] || row[fieldMapping.name].trim() === '') {
          errors.push(`Row ${rowNum}: Missing name`)
          return
        }
        
        // Check coordinates
        if (coordinateMode.value === 'combined') {
          const coords = row[fieldMapping.geometry]
          if (!coords || coords.trim() === '') {
            errors.push(`Row ${rowNum}: Missing coordinates`)
            return
          }
          
          // Try to parse coordinates
          try {
            if (coords.includes(',')) {
              const parts = coords.split(',').map(p => parseFloat(p.trim()))
              if (parts.length !== 2 || parts.some(isNaN)) {
                errors.push(`Row ${rowNum}: Invalid coordinate format`)
                return
              }
            } else {
              errors.push(`Row ${rowNum}: Invalid coordinate format`)
              return
            }
          } catch (e) {
            errors.push(`Row ${rowNum}: Invalid coordinate format`)
            return
          }
        } else {
          const lat = row[fieldMapping.latitude]
          const lng = row[fieldMapping.longitude]
          
          if (!lat || !lng || lat.trim() === '' || lng.trim() === '') {
            errors.push(`Row ${rowNum}: Missing latitude or longitude`)
            return
          }
          
          const latNum = parseFloat(lat)
          const lngNum = parseFloat(lng)
          
          if (isNaN(latNum) || isNaN(lngNum)) {
            errors.push(`Row ${rowNum}: Invalid latitude or longitude values`)
            return
          }
          
          if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
            errors.push(`Row ${rowNum}: Latitude/longitude out of valid range`)
            return
          }
        }
        
        validCount++
      })
      
      validationResults.valid = validCount
      validationResults.errors = errors
      
      step.value = 3
    }
    
    const validateWithGeocoding = async () => {
      try {
        step.value = 4 // Show loading during geocoding
        
        // Note: addressMapping structure is defined for documentation
        // but not used directly in this implementation
        
        const addresses = csvData.value.map((row, index) => {
          const addressData = { index }
          
          if (addressMode.value === 'single') {
            addressData.fullAddress = row[fieldMapping.fullAddress] || ''
          } else {
            // Components
            if (fieldMapping.street) addressData.street = row[fieldMapping.street] || ''
            if (fieldMapping.city) addressData.city = row[fieldMapping.city] || ''
            if (fieldMapping.state) addressData.state = row[fieldMapping.state] || ''
            if (fieldMapping.country) addressData.country = row[fieldMapping.country] || ''
            if (fieldMapping.postalCode) addressData.postal_code = row[fieldMapping.postalCode] || ''
          }
          
          return addressData
        })
        
        // Geocode addresses
        const geocodingResponse = await GeocodingService.geocodeAddresses(
          addresses, 
          addressMode.value, 
          geocodingConfig.countryCode || null
        )
        
        geocodingResults.value = geocodingResponse.results
        
        // Note: validation results are available but not used in current implementation
        // const validation = GeocodingService.validateGeocodingResults(geocodingResponse.results)
        
        // Count valid names as well
        let validCount = 0
        const errors = []
        
        csvData.value.forEach((row, index) => {
          const rowNum = index + 1
          
          // Check name
          if (!row[fieldMapping.name] || row[fieldMapping.name].trim() === '') {
            errors.push(`Row ${rowNum}: Missing name`)
            return
          }
          
          // Check if geocoding was successful
          const geocodingResult = geocodingResults.value.find(r => r.index === index)
          if (!geocodingResult || !geocodingResult.success) {
            errors.push(`Row ${rowNum}: Geocoding failed - ${geocodingResult?.error || 'Unknown error'}`)
            return
          }
          
          validCount++
        })
        
        validationResults.valid = validCount
        validationResults.errors = errors
        
        step.value = 3 // Go to configuration step
        
      } catch (err) {
        error.value = err.message || 'Geocoding validation failed'
        step.value = 2 // Go back to mapping step
      }
    }

    const uploadCollection = async () => {
      try {
        step.value = 4 // Show loading
        
        // Reset progress
        uploadProgress.current = 0
        uploadProgress.total = csvData.value.length
        uploadProgress.message = 'Starting upload...'
        uploadProgress.status = 'starting'
        uploadProgress.errors = []
        
        // Prepare field mapping with geocoding data if needed
        const enhancedFieldMapping = { ...fieldMapping }
        
        if (coordinateMode.value === 'geocoding') {
          enhancedFieldMapping.geocoding = {
            mode: addressMode.value,
            fullAddress: fieldMapping.fullAddress,
            components: {
              street: fieldMapping.street,
              city: fieldMapping.city,
              state: fieldMapping.state,
              country: fieldMapping.country,
              postal_code: fieldMapping.postalCode
            },
            countryCode: geocodingConfig.countryCode
          }
        }
        
        const uploadData = {
          name: collectionConfig.name,
          description: collectionConfig.description,
          locations: csvData.value,
          fieldMapping: enhancedFieldMapping,
          progressTracking: true  // Enable progress tracking
        }
        
        const response = await LocationsService.createLocationCollection(uploadData)
        
        // Check if response indicates progress tracking mode
        if (response.data.upload_id) {
          // Start progress polling
          await pollUploadProgress(response.data.upload_id, response.data.collection_id)
        } else {
          // Immediate response (legacy mode)
          uploadResult.value = response.data
          step.value = 5 // Show success
        }
        
      } catch (err) {
        error.value = err.response?.data?.error || err.message || 'Failed to upload collection'
        step.value = 3
      }
    }
    
    const pollUploadProgress = async (uploadId, collectionId) => {
      try {
        // Poll progress endpoint
        const pollProgress = async () => {
          try {
            const response = await fetch(`${process.env.VUE_APP_API_URL}/api/locations/collections/progress/${uploadId}`)
            
            if (!response.ok) {
              throw new Error('Progress polling failed')
            }
            
            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            
            let isReading = true
            while (isReading) {
              const { done, value } = await reader.read()
              
              if (done) {
                isReading = false
                break
              }
              
              const chunk = decoder.decode(value)
              const lines = chunk.split('\\n\\n')
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const progressData = JSON.parse(line.slice(6))
                    
                    // Update progress
                    uploadProgress.current = progressData.current || 0
                    uploadProgress.total = progressData.total || csvData.value.length
                    uploadProgress.message = progressData.message || 'Processing...'
                    uploadProgress.status = progressData.status || 'processing'
                    uploadProgress.errors = progressData.errors || []
                    
                    // Check if complete
                    if (progressData.status === 'completed') {
                      uploadResult.value = {
                        id: collectionId,
                        locations_created: progressData.locations_created || progressData.current,
                        errors: progressData.errors
                      }
                      step.value = 5 // Show success
                      return
                    } else if (progressData.status === 'error') {
                      error.value = progressData.message || 'Upload failed'
                      step.value = 3
                      return
                    }
                  } catch (parseError) {
                    console.warn('Failed to parse progress data:', parseError)
                  }
                }
              }
            }
          } catch (err) {
            console.error('Progress polling error:', err)
            error.value = 'Failed to track upload progress'
            step.value = 3
          }
        }
        
        await pollProgress()
        
      } catch (err) {
        console.error('Progress tracking error:', err)
        error.value = 'Upload progress tracking failed'
        step.value = 3
      }
    }

    const finishUpload = () => {
      emit('collection-uploaded', {
        name: collectionConfig.name,
        id: uploadResult.value.id,
        locations_created: uploadResult.value.locations_created
      })
      emit('close')
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const formatPreviewValue = (value) => {
      if (value == null) return ''
      const str = String(value)
      return str.length > 20 ? str.substring(0, 17) + '...' : str
    }

    return {
      step,
      selectedFile,
      isDragOver,
      error,
      fileInput,
      csvData,
      csvColumns,
      coordinateMode,
      addressMode,
      fieldMapping,
      geocodingConfig,
      collectionConfig,
      validationResults,
      uploadResult,
      geocodingResults,
      showGeocodingPreview,
      uploadProgress,
      isMappingValid,
      handleFileDrop,
      handleFileSelect,
      triggerFileInput,
      removeFile,
      processFile,
      validateMapping,
      uploadCollection,
      finishUpload,
      formatFileSize,
      formatPreviewValue
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 1.5rem;
}

.file-drop-zone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
}

.file-drop-zone:hover,
.file-drop-zone.drag-over {
  border-color: #007bff;
  background: #f8f9fa;
}

.drop-zone-content {
  color: #6c757d;
}

.upload-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.file-types {
  font-size: 0.875rem;
  color: #adb5bd;
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #e3f2fd;
  border-radius: 4px;
}

.file-icon {
  font-size: 2rem;
}

.file-info {
  flex: 1;
  text-align: left;
}

.file-name {
  font-weight: 600;
  color: #495057;
}

.file-size {
  color: #6c757d;
  font-size: 0.875rem;
}

.btn-remove {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #dc3545;
  padding: 0.25rem;
}

.mapping-form {
  margin-bottom: 1.5rem;
}

.mapping-instructions {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.field-mappings {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.mapping-group {
  border: 1px solid #dee2e6;
  padding: 1rem;
  border-radius: 4px;
}

.mapping-group.required {
  border-left: 4px solid #dc3545;
}

.mapping-group.optional {
  border-left: 4px solid #28a745;
}

.mapping-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}

.coordinate-options {
  margin-top: 0.5rem;
}

.option-group {
  margin-bottom: 1rem;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: normal;
}

.lat-lng-selects {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 0.5rem;
}

.lat-lng-field label {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

/* Geocoding styles */
.geocoding-options {
  margin-top: 0.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #f8f9fa;
}

.address-mode-selection {
  margin-bottom: 1rem;
}

.address-field-select {
  margin: 0.5rem 0 1rem 1.5rem;
}

.address-field-select label {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.address-components {
  margin: 0.5rem 0 0 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.component-field label {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  font-weight: normal;
}

.geocoding-settings {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.form-input.small {
  max-width: 150px;
}

.geocoding-settings small {
  display: block;
  color: #6c757d;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.form-select,
.form-input,
.form-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.config-form {
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}

.validation-summary {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.summary-stats {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stat-label {
  font-weight: 600;
  color: #495057;
}

.stat-value.success {
  color: #28a745;
  font-weight: bold;
}

.stat-value.error {
  color: #dc3545;
  font-weight: bold;
}

.validation-errors {
  margin-top: 1rem;
}

.validation-errors h5 {
  margin: 0 0 0.5rem 0;
  color: #dc3545;
}

.error-list {
  margin: 0;
  padding-left: 1.5rem;
  color: #dc3545;
  font-size: 0.875rem;
}

.preview-section {
  margin-top: 1.5rem;
}

.preview-section h4 {
  margin: 0 0 0.5rem 0;
  color: #495057;
}

.preview-info {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.preview-table {
  max-height: 200px;
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.preview-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.preview-table th,
.preview-table td {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  text-align: left;
}

.preview-table th {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
}

.preview-note {
  padding: 0.5rem;
  font-size: 0.75rem;
  color: #6c757d;
  text-align: center;
  background: #f8f9fa;
  margin: 0;
}

.loading-content,
.success-content {
  text-align: center;
  padding: 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

/* Progress Bar Styles */
.progress-section {
  margin: 1.5rem 0;
}

.progress-bar {
  width: 100%;
  height: 20px;
  background-color: #f8f9fa;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.5rem;
  border: 1px solid #dee2e6;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
  border-radius: 10px;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-text {
  text-align: center;
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

.progress-errors {
  margin-top: 1rem;
  text-align: left;
}

.progress-errors details {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 0.75rem;
}

.progress-errors summary {
  cursor: pointer;
  font-weight: 500;
  color: #856404;
  margin-bottom: 0.5rem;
}

.progress-errors ul {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  font-size: 0.875rem;
  color: #6c757d;
  max-height: 150px;
  overflow-y: auto;
}

.progress-errors li {
  margin-bottom: 0.25rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.upload-summary {
  margin-top: 1.5rem;
  text-align: left;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}

.summary-item label {
  font-weight: 600;
  color: #495057;
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: white;
  color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background: #6c757d;
  color: white;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  margin-top: 1rem;
}

.error-icon {
  font-size: 1.25rem;
}
</style>